import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { NwvoteService } from './nwvote.service';
import { ModalService } from '../modal/modal.service';
import { NAME_VOTE_STORE } from '../signals/+store/name-vote.store';
import { ResponseData, NameData } from '../types/name-vote.model';
import { HISTORY_BOOK_STORE } from '../signals/+store/history-book.store';

@Component({
    selector: 'app-nw-vote',
    templateUrl: './nw-vote.component.html',
    styleUrls: ['./nw-vote.component.scss'],
    standalone: false
})
export class NwVoteComponent implements OnInit, OnDestroy {
  private readonly nameVoteStore = inject(NAME_VOTE_STORE);
  private readonly historyBookStore = inject(HISTORY_BOOK_STORE);
  private readonly nwvoteService = inject(NwvoteService);
  private readonly modalService = inject(ModalService);

  name = '';
  readOnlyName = false;
  projectName = '';
  userToken = '';
  userName = '';
  email = '';
  progressBarCompletion = { width: '0%' };
  progressBarValue = '0';
  positiveVote = false;
  negativeVote = false;
  neutralVote = false;
  rationales = '';
  notation: string;
  nameToDisplay: string;
  currentSlide = 0;
  isNonProp = false;
  private intervalId!: ReturnType<typeof setInterval>;

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  ngOnInit(): void {
    this.callNewName();

    this.intervalId = setInterval(() => this.callNewName(), 3000);

    // Clear the store if the project name has changed
    const currentProjectName = localStorage.getItem('project');
    if (this.projectName && this.projectName !== currentProjectName) {
      this.nameVoteStore.clearStore();
    }

    this.projectName = currentProjectName;

    this.nameVoteStore.updateProject(this.projectName);
    this.userToken = localStorage.getItem('userTokenId');
    this.email = localStorage.getItem('username');
  }

  openModal(modalTemplate: TemplateRef<string>, title?: string) {
    this.nameVoteStore.updateName(title);
    this.modalService.open(modalTemplate, {
      size: '380px',
    });
  }

  callNewName() {
    this.nwvoteService.getName().subscribe((res: ResponseData) => {
      const parsedData: NameData[] = JSON.parse(res?.d);
      const currentName = parsedData[0].currentName;
      const currentProjectName = localStorage.getItem('project');
      if(parsedData[0].presentationType === 'Nonproprietary'){
        this.isNonProp = true;
      }

      // Clear the store if the project name has changed
      if (this.projectName !== currentProjectName) {
        this.nameVoteStore.clearStore();
        this.projectName = currentProjectName;
      }

      if (this.name !== currentName) {
        this.initializeNewNameData(currentName);
      }

      this.currentSlide = parsedData[0].currentSlide;
      this.name = currentName;
      if (currentName.includes('##') || currentName.includes('$$')) {
        this.nameToDisplay =
          'Please discuss the current names with your Brand Institute moderator';
        this.notation = '';
        this.rationales = '';
      } else {
        // Remove the notation from the name to display
        this.nameToDisplay = currentName.split(/[\|\(]/)[0].trim();
        this.notation = currentName.split('|')[1];
        this.rationales = parsedData[0].nameRationale.replace('`', "'");
      }
      this.readOnlyName = parsedData[0].readOnly === 1;
      this.progressBarValue = parsedData[0].completion;
      this.progressBarCompletion = {
        width: this.progressBarValue + '%',
      };
    });
  }

  initializeNewNameData(currentName: string) {
    const existingData = this.nameVoteStore.nameVoteData()?.[currentName];

    if (existingData) {
      this.positiveVote = existingData.vote === 'positive';
      this.negativeVote = existingData.vote === 'negative';
      this.neutralVote = existingData.vote === 'neutral';
    } else {
      this.positiveVote = false;
      this.negativeVote = false;
      this.neutralVote = false;
    }

    this.nameVoteStore.updateName(currentName);
  }

  setVote(voteType: string) {
    this[voteType + 'Vote'] = true;
    this.positiveVote = voteType === 'positive';
    this.negativeVote = voteType === 'negative';
    this.neutralVote = voteType === 'neutral';

    this.nameVoteStore.updatenameVoteData({
      name: this.name,
      vote: voteType,
      slideNumber: this.currentSlide,
    });

    const historyData = {
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      action: 'Voted',
      content: JSON.stringify({
        'For Name': this.name,
        Vote: voteType.charAt(0).toUpperCase() + voteType.slice(1),
      }),
    };

    this.historyBookStore.addHistory(historyData);
    this.sendVote(voteType);
  }

  removeVote() {
    this.positiveVote = false;
    this.negativeVote = false;
    this.neutralVote = false;

    this.nameVoteStore.updatenameVoteData({
      name: this.name,
      vote: '',
      slideNumber: this.currentSlide,
    });

    const historyData = {
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      action: 'Removed Vote',
      content: JSON.stringify({
        'For Name': this.name,
      }),
    };

    this.historyBookStore.addHistory(historyData);
    this.sendVote('');
  }

  sendVote(vote: string) {
    this.nwvoteService
      .voteName(this.name, vote)
      .subscribe((res: ResponseData) => {});
  }
}
